import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';

const ForcePasswordChange: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Le password non corrispondono');
      return;
    }

    if (password.length < 6) {
      setError('La password deve contenere almeno 6 caratteri');
      return;
    }

    if (!user) {
      setError('Utente non trovato');
      return;
    }

    setLoading(true);

    try {
      // Usa l'endpoint force-change che non richiede vecchia password
      await authAPI.forcePasswordChange(password);
      
      setSuccess(true);
      setError('');
      
      // Refresh user data per aggiornare must_change_password
      await refreshUser();
      
      // Aspetta 1 secondo per mostrare il messaggio di successo, poi naviga
      setTimeout(() => {
        // Passa sempre dalla root: la dashboard effettiva viene scelta
        // centralmente in base a ruolo e perimetro amministrativo.
        navigate('/');
      }, 1000);
    } catch (err: any) {
      // Errore('Errore cambio password:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Errore durante il cambio password';
      setError(errorMessage);
      setSuccess(false);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url(/calendar-5983133_1920.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(13, 71, 161, 0.7)',
        }
      }}
    >
      <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              padding: 4, 
              width: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography component="h1" variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                Cambio Password Obbligatorio
              </Typography>
              <Alert severity="warning" sx={{ width: '100%', mb: 3, mt: 2 }}>
                È necessario cambiare la password prima di continuare.
              </Alert>

              {success && (
                <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                  Password modificata con successo! Verrai reindirizzato alla dashboard...
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Nuova Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading || success}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Conferma Password"
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading || success}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ 
                    mt: 3, 
                    mb: 2,
                    backgroundColor: '#0d47a1',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    }
                  }}
                  disabled={loading || success}
                >
                  {loading ? <CircularProgress size={24} /> : 'Cambia Password'}
                </Button>
              </Box>
          </Box>
        </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default ForcePasswordChange;

